/**
 * Ready-to-use prompts.
 *
 * These are a shortcut to a *starting point*, never a configuration step. A
 * selected prompt lands in the box as ordinary editable text, because the whole
 * product promise is that a description is enough — the moment the gallery
 * becomes a set of templates you pick between, Hatch has grown options it
 * deliberately does not have.
 *
 * Static by design. Nothing here needs a server: there is no personalisation,
 * no per-user history, and an endpoint would be a moving part with no
 * requirement behind it.
 *
 * WORK, not hobbies. Every card describes something a business would actually
 * run — a client portal, a pipeline dashboard, an asset register — and that is
 * a deliberate reversal of where this file started. The cards used to be
 * consumer-shaped: a barbershop, a grandmother's recipes, a five-a-side
 * tournament. They demo the same machinery, and they read at a booth as a toy.
 * Someone standing at AI TechWorld deciding whether Hatch can build THEIR
 * internal tool needs to see their own problem on the card, so a card that is
 * charming and off-audience costs more than a plain one that lands.
 *
 * That is a constraint on what gets added next, not just a description of what
 * is here: a delightful personal-project card is exactly the kind of thing that
 * creeps back in one at a time.
 */

export interface GalleryPrompt {
  /** Short label for the card. */
  title: string;
  /** The full text placed in the prompt box. */
  prompt: string;
}

export interface GalleryGroup {
  /** Grouping so the collection is browsable rather than a flat wall. */
  name: string;
  prompts: GalleryPrompt[];
}

export const PROMPT_GALLERY: GalleryGroup[] = [
  {
    name: "Business",
    prompts: [
      {
        title: "Appointment booking",
        prompt:
          "A booking page for a consulting firm where clients schedule discovery calls, with service offerings, rates, and an enquiry form",
      },
      {
        title: "Product catalogue",
        prompt:
          "A product catalogue for a wholesale supplier with items grouped by category, each with a code, unit price, and minimum order quantity",
      },
      {
        title: "Case studies",
        prompt:
          "A case study library for an agency where each project lists the client, the problem, what was delivered, and the result, filterable by industry",
      },
    ],
  },
  {
    name: "Operations",
    prompts: [
      {
        title: "Inventory tracker",
        prompt:
          "An inventory tracker for a warehouse with stock levels per item, a low-stock filter, and a note of when each item was last counted",
      },
      {
        title: "Expense log",
        prompt:
          "An expense log where staff submit a claim with a date, category, amount, and description, and see the running total for each month",
      },
      {
        title: "Asset register",
        prompt:
          "An asset register for company laptops and monitors, each with an owner, serial number, purchase date, and condition",
      },
    ],
  },
  {
    name: "Team",
    prompts: [
      {
        title: "Kanban board",
        prompt:
          "A kanban board for a client project with To Do, In Progress, and Done columns, where team members claim tasks with their name and email and move them between stages",
      },
      {
        title: "Onboarding checklist",
        prompt:
          "An onboarding checklist for new hires with tasks grouped by first day, first week, and first month, where a manager ticks items off as they are done",
      },
      {
        title: "Meeting notes",
        prompt:
          "A meeting notes archive with an entry per meeting recording the date, attendees, decisions, and action items, searchable by attendee",
      },
    ],
  },
  {
    // Live-updating sites, described by what a visitor SEES rather than by the
    // technology underneath. Naming realtime here would turn the gallery into a
    // feature menu — the thing this file's header comment forbids — and it is
    // also unnecessary: the build prompt reaches for sockets when the described
    // site is chat-, feed-, or presence-shaped, which is exactly what these say.
    name: "Live",
    prompts: [
      {
        title: "Support chat",
        prompt:
          "A support chat where a customer types a question and whoever is on duty answers, with messages appearing for both sides the moment they are sent and a list of who is currently here",
      },
      {
        title: "KPI dashboard",
        prompt:
          "A KPI dashboard for a sales team tracking pipeline, revenue, and win rate, where metrics update on the screen as deals close, without anyone refreshing",
      },
      {
        title: "Retro board",
        prompt:
          "A sprint retro board where the team adds cards under what went well and what to improve, and everyone watching sees new cards appear straight away",
      },
    ],
  },
  {
    name: "Tools",
    prompts: [
      {
        title: "Feedback board",
        prompt:
          "A product feedback board where customers submit ideas and upvote existing ones, sorted by votes",
      },
      {
        title: "Resource directory",
        prompt:
          "A directory of internal tools and documentation, grouped by team, each with a title, link, and one-line description",
      },
      {
        title: "Changelog",
        prompt:
          "A public changelog with dated releases, each tagged as a feature, fix, or improvement, filterable by tag",
      },
    ],
  },
];
